import { useCallback, useEffect, useState } from 'react';

import {
  getCustomerDishtypes,
  updateCustomerDishtypes,
  getDishtypes,
} from '../utils/api';
import { createSelectorOptions } from '../utils/utils';
import Selector from './Selector';

interface DishtypePreferencesProps {
  onChange?: (
    selectedRecipes: number[],
    dishtypesOptions: SelectorOptions
  ) => void;
}

type SelectorOptions = Record<
  string,
  { value: number; optionText: string; [key: string]: unknown }
>;

const DishtypePreferences = ({ onChange }: DishtypePreferencesProps) => {
  const [selectedRecipes, setSelectedRecipes] = useState<number[]>([]);
  const [dishtypesOptions, setDishtypesOptions] = useState<SelectorOptions>({});

  const passDataToParent = useCallback(
    (recipes: number[], options: SelectorOptions) => {
      onChange?.(recipes, options);
    },
    [onChange]
  );

  useEffect(() => {
    let cancelled = false;

    getDishtypes()
      .then((data: unknown) => {
        const options = createSelectorOptions(
          data,
          'id',
          'name',
          'profile_image',
          undefined
        ) as SelectorOptions;

        if (cancelled) return;
        setDishtypesOptions(options);
        return getCustomerDishtypes().then((custData) => ({
          options,
          custData,
        }));
      })
      .then(
        (
          result:
            | { options: SelectorOptions; custData: { dishtypes: number[] } }
            | undefined
        ) => {
          if (cancelled || !result) return;
          const { options, custData } = result;
          setSelectedRecipes(custData.dishtypes);
          passDataToParent(custData.dishtypes, options);
        }
      )
      // eslint-disable-next-line no-console
      .catch(console.error);

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updatePrefs = (val: number) => {
    const isSelected = selectedRecipes.includes(val);
    const rating = isSelected ? 0 : 1;

    return updateCustomerDishtypes(val, rating).then(
      ({ id }: { id: number }) => {
        setSelectedRecipes((prev) => {
          const next = isSelected
            ? prev.filter((d) => d !== id)
            : [...prev, id];
          passDataToParent(next, dishtypesOptions);
          return next;
        });
      }
    );
  };

  return (
    <Selector
      selected={selectedRecipes}
      options={dishtypesOptions}
      action={(val) => {
        if (typeof val === 'number') return updatePrefs(val);
      }}
    />
  );
};

export default DishtypePreferences;
